import timeService, { DATE_FORMAT } from "../../services/time/timeService"
import { convertStringToJSON } from "../Strings/stringHelpers"
import { BookingFeeEntry } from "../../Types/bookingTypes"

export const confirmedExtensionPattern = /^extend-stay-.+-confirmed-\d+$/

export const splitBookingFees = (fees: any[] | undefined, splitCount: number): BookingFeeEntry[] => {
  if (!fees || fees.length === 0 || splitCount < 1) {
    return []
  }
  return fees.map((fee) => ({
    feeType: fee.feeType,
    feeSource: fee.feeSource,
    feeAmount: Number(((Number(fee.feeAmount) || 0) / splitCount).toFixed(2)),
  }))
}

export const getConfirmedExtensions = (booking: any) => {
    return (booking?.linkedServices || []).reduce((extensions: any[], service: any) => {
        if (!confirmedExtensionPattern.test(service.linkedService || '')) {
            return extensions
        }

        const info = convertStringToJSON(service.linkedServiceId)
        if (info?.confirmed) {
            extensions.push({
                ...info,
                linkedService: service.linkedService,
                payout: info.extensionEarnings ?? service.linkedServicePrice ?? 0,
            })
        }

        return extensions
    }, []).sort((first: any, second: any) => first.confirmed - second.confirmed)
}

export const bookingWithoutExtensions = (booking: any) => {
    const extension = getConfirmedExtensions(booking)[0]

    if (!extension) {
        return booking
    }

    return {
        ...booking,
        checkOut: extension.originalCheckOut || booking.checkOut,
        earnings: extension.originalEarnings ?? booking.earnings,
        pricePerNight: extension.originalPricePerNight ?? booking.pricePerNight,
    }
}

const roundMoney = (value: number) => +(+value || 0).toFixed(2)

const pricePerNightFor = (earnings: number, checkIn: any, checkOut: any, fallback: number = 0) => {
    const nights = timeService.calculateNights(checkIn, checkOut)
    return nights > 0 ? roundMoney(earnings / nights) : fallback
}

// booking.earnings = base earnings + confirmed extension earnings
export const getBaseEarnings = (booking: any) => {
    const extensionTotal = getConfirmedExtensions(booking).reduce((total: number, extension: any) => total + (+extension.payout || 0), 0)
    return roundMoney((+booking?.earnings || 0) - extensionTotal)
}

// Re-chains every confirmed extension from the base earnings so the booking totals stay consistent
export const buildEarningsUpdate = (
    booking: any,
    baseEarnings: number,
    extensionOverrides: { [linkedService: string]: number } = {}
) => {
    const extensions = getConfirmedExtensions(booking)
    const rebuiltServices: { [linkedService: string]: any } = {}
    let runningEarnings = roundMoney(baseEarnings)

    extensions.forEach((extension: any) => {
        const extensionEarnings = roundMoney(extensionOverrides[extension.linkedService] ?? extension.payout)
        const newEarnings = roundMoney(runningEarnings + extensionEarnings)
        const { linkedService, payout, ...info } = extension

        rebuiltServices[linkedService] = {
            linkedServiceId: JSON.stringify({
                ...info,
                originalEarnings: runningEarnings,
                originalPricePerNight: pricePerNightFor(runningEarnings, booking.checkIn, info.originalCheckOut, info.originalPricePerNight),
                extensionEarnings,
                newEarnings,
                newPricePerNight: pricePerNightFor(newEarnings, booking.checkIn, info.newCheckOut, info.newPricePerNight),
            }),
            linkedServicePrice: extensionEarnings,
        }
        runningEarnings = newEarnings
    })

    return {
        earnings: runningEarnings,
        pricePerNight: pricePerNightFor(runningEarnings, booking.checkIn, booking.checkOut, +booking.pricePerNight || 0),
        linkedServices: (booking.linkedServices || []).map((service: any) => (
            rebuiltServices[service.linkedService] ? { ...service, ...rebuiltServices[service.linkedService] } : service
        )),
    }
}

export const bookingsByDayObject = (bookingsList: any[] | null) => {
    const hashedData: any = {}
    if (!bookingsList || bookingsList === null) {
        return hashedData
    }
    [...bookingsList].sort((x, y) => {
        return timeService.dateToMillis(x.checkIn) - timeService.dateToMillis(y.checkIn)
    }).forEach((x: any) => {
        const nights = timeService.calculateNights(x.checkIn, x.checkOut)
        const leadTime = Math.max(0, Math.floor(timeService.timeDiff(x.dateBooked, x.checkIn) || 0))
        const payoutEarnings = bookingWithoutExtensions(x).earnings || 0

        let bookingStartDate = timeService.createLuxonDate(x.checkIn).minus({ days: 1 })
        const bookingEndDateString = timeService.getFormattedTime(x.checkOut, DATE_FORMAT)
        const bookingEndUnix = timeService.dateToMillis(x.checkOut)
        while (bookingStartDate.toFormat(DATE_FORMAT) !== bookingEndDateString && bookingStartDate.toMillis() < bookingEndUnix) {
            bookingStartDate = bookingStartDate.plus({ days: 1 })
            const currentDayString = bookingStartDate.toFormat(DATE_FORMAT)
            if (!hashedData[currentDayString]) {
                hashedData[currentDayString] = {
                    date: currentDayString,
                    booking: {},
                    paid: {},
                    payout: 0,
                    sleeps: 0,
                }
            }
            if (currentDayString === bookingEndDateString) {
                hashedData[currentDayString].checkingOut = x
            } else {
                // check if there are any overlapping bookings for the same day and if so, add it to an array of overlaps instead of overwriting the existing booking
                if (hashedData[currentDayString].booking && hashedData[currentDayString].booking._id) {
                    if (!hashedData[currentDayString].overlaps) {
                        hashedData[currentDayString].overlaps = [hashedData[currentDayString].booking]
                    }
                    hashedData[currentDayString].overlaps.push(x)
                    hashedData[currentDayString].sleeps += 1
                    hashedData[currentDayString].earnings += x.earnings / nights
                    hashedData[currentDayString].totalEarning += x.earnings
                } else {
                    // no overlaps, just add the booking to the day
                    hashedData[currentDayString].booking = x
                    hashedData[currentDayString].sleeps = 1
                    hashedData[currentDayString].earnings = x.earnings / nights
                    hashedData[currentDayString].totalEarning = x.earnings
                    hashedData[currentDayString].leadTime = leadTime
                }

                if (currentDayString === timeService.getFormattedTime(x.checkIn, DATE_FORMAT) && !dayOfPayouts[x.source]) {
                    const checkInDate = timeService.createLuxonDate(x.checkIn)
                    const isEndOfMonth = checkInDate.day === checkInDate.daysInMonth
                    const payoutDateString = isEndOfMonth
                        ? currentDayString
                        : checkInDate.plus({ days: 1 }).toFormat(DATE_FORMAT)

                    if (!hashedData[payoutDateString]) {
                        hashedData[payoutDateString] = {
                            date: payoutDateString,
                            booking: {},
                            paid: {},
                            payout: 0,
                        }
                    }
                    hashedData[payoutDateString].payout += payoutEarnings
                    hashedData[payoutDateString].paid[x._id] = 1
                }
            }

            // this is for day of payouts which are generally direct bookings or through methods that pay instantly like zelle, cash, paypal, etc.
            if (dayOfPayouts[x.source] && currentDayString === timeService.getFormattedTime(x.checkIn, DATE_FORMAT)) {
                const hashedDate = timeService.createLuxonDate(x.dateBooked).toFormat(DATE_FORMAT)
                if (!hashedData[hashedDate]) {
                    hashedData[hashedDate] = {
                        date: hashedDate,
                        booking: {},
                        paid: {},
                        payout: 0,
                    }
                }
                if (!hashedData[hashedDate].payout) {
                    hashedData[hashedDate].payout = 0
                }
                if (!hashedData[hashedDate].paid[x._id]) {
                    hashedData[hashedDate].payout += payoutEarnings
                    hashedData[hashedDate].paid[x._id] = 1
                }
            }

        }

        if (x.linkedServices && Array.isArray(x.linkedServices)) {
            x.linkedServices
                .filter((service: any) => /^extend-stay-.*-confirmed-.+$/.test(service.linkedService || ''))
                .forEach((service: any) => {
                    const extensionInfo: any = convertStringToJSON(service.linkedServiceId)

                    if (!extensionInfo?.confirmed) {
                        return
                    }

                    const extensionStartDate = timeService.createLuxonDate(new Date(extensionInfo.originalCheckOut || x.checkOut)).toFormat(DATE_FORMAT)
                    if (!hashedData[extensionStartDate]) {
                        hashedData[extensionStartDate] = {
                            date: extensionStartDate,
                            booking: {},
                            paid: {},
                            payout: 0,
                        }
                    }
                    if (!hashedData[extensionStartDate].extensions) {
                        hashedData[extensionStartDate].extensions = []
                    }
                    hashedData[extensionStartDate].extensions.push({
                        booking: x,
                        service,
                        info: extensionInfo,
                    })

                    if (!hashedData[extensionStartDate]) {
                        hashedData[extensionStartDate] = {
                            date: extensionStartDate,
                            booking: {},
                            paid: {},
                            payout: 0,
                        }
                    }
                    hashedData[extensionStartDate].payout += extensionInfo.extensionEarnings ?? service.linkedServicePrice ?? 0
                })
        }

        // add some logic here for the last date
    })
    return hashedData
}

// currently only 'dayOfPayouts' is considered as day of payout which is only a placeholder incase we want to bring back other methods later
export const dayOfPayouts: { [key: string]: number } = {
    // 'website': 1,
    // 'direct': 1,

    // 'zelle': 1,
    // 'cash': 1,
    // 'wire': 1,
    // 'paypal': 1,

    'dayOfPayouts': 1,
}